import { createContext, useContext, useMemo, useRef, useState } from 'react';

type MultiReferenceResetFunction = () => void;

type FormResetContext = {
    isResetting: boolean;
    setIsResetting: (v: boolean) => void;
    multiReferenceResetFunctions: React.MutableRefObject<Set<MultiReferenceResetFunction>>;
    registerMultiReferenceReset: (fn: MultiReferenceResetFunction) => () => void;
    resetAllMultiReferences: () => void;
};
const FormResetContext = createContext<FormResetContext | null>(null);

export const FormResetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isResetting, setIsResetting] = useState(false);
    const multiReferenceResetFunctions = useRef<Set<MultiReferenceResetFunction>>(new Set());

    const registerMultiReferenceReset = (fn: MultiReferenceResetFunction) => {
        multiReferenceResetFunctions.current.add(fn);
        return () => {
            multiReferenceResetFunctions.current.delete(fn);
        };
    };

    const resetAllMultiReferences = () => {
        multiReferenceResetFunctions.current.forEach(fn => fn());
    };

    const value = useMemo(() => ({
        isResetting,
        setIsResetting,
        multiReferenceResetFunctions,
        registerMultiReferenceReset,
        resetAllMultiReferences
    }), [isResetting]);
    return <FormResetContext.Provider value={value}>{children}</FormResetContext.Provider>;
};

export const useFormReset = () => {
    const ctx = useContext(FormResetContext);
    if (!ctx) throw new Error('useFormReset must be used inside <FormResetProvider>');
    return ctx;
};
